import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../components/DashboardLayout";
import { professionalAPI } from "../services/api";

export default function ProfessionalPage() {
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.Auth);

    const [profileData, setProfileData] = useState({});
    const [formData, setFormData] = useState({
        experience: "",
        specializations: [],
        skills: [],
        certifications: [],
        achievements: "",
    });

    const [availableSkills, setAvailableSkills] = useState([]);

    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [showCertModal, setShowCertModal] = useState(false);
    const [newCert, setNewCert] = useState({
        name: "",
        issuedBy: "",
        year: "",
        file: null,
    });

    const specializationOptions = [
        { id: 1, name: "Weight Loss", icon: "bi-arrow-down-circle" },
        { id: 2, name: "Muscle Building", icon: "bi-lightning-charge" },
        { id: 3, name: "Yoga", icon: "bi-heart-pulse" },
        { id: 4, name: "CrossFit", icon: "bi-trophy" },
        { id: 5, name: "Cardio Training", icon: "bi-activity" },
        { id: 6, name: "Strength Training", icon: "bi-shield-check" },
        { id: 7, name: "HIIT", icon: "bi-fire" },
        { id: 8, name: "Pilates", icon: "bi-person-arms-up" },
        { id: 9, name: "Functional Training", icon: "bi-gear" },
        { id: 10, name: "Sports Specific", icon: "bi-dribbble" },
        { id: 11, name: "Nutrition Coaching", icon: "bi-apple" },
        { id: 12, name: "Rehabilitation", icon: "bi-bandaid" },
    ];

    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (!storedUser && !isAuthenticated) {
            navigate("/login");
            return;
        }

        // Fetch professional data from API
        const fetchProfessionalData = async () => {
            try {
                setFetchingData(true);
                const response = await professionalAPI.getProfessional();

                if (response.data.success) {
                    const data = response.data.data;
                    console.log(data);

                    setFormData({
                        experience: data.experience || "",
                        specializations: data.specializations || [],
                        skills: data.skills || [],
                        certifications: data.certifications || [],
                        achievements: data.achievements || "",
                    });
                }

                // Fetch available skills
                const skillsResponse = await professionalAPI.getSkills();
                if (skillsResponse.data.success) {
                    setAvailableSkills(skillsResponse.data.list || []);
                }
            } catch (error) {
                console.error("Error fetching professional data:", error);
                if (error.response?.status !== 404) {
                    toast.error("Failed to load professional details");
                }
            } finally {
                setFetchingData(false);
            }
        };

        fetchProfessionalData();
    }, [navigate, isAuthenticated]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSpecializationToggle = (specialization) => {
        setFormData((prev) => {
            const specializations = prev.specializations.find((s) => s.id === specialization.id)
                ? prev.specializations.filter((s) => s.id !== specialization.id)
                : [...prev.specializations, specialization];
            return { ...prev, specializations };
        });
    };

    const handleSkillToggle = (skill) => {
        setFormData((prev) => {
            const exists = prev.skills.find(s => s.id === skill.id || s === skill.id);
            let newSkills;
            if (exists) {
                newSkills = prev.skills.filter(s => (s.id || s) !== skill.id);
            } else {
                newSkills = [...prev.skills, skill];
            }
            return { ...prev, skills: newSkills };
        });
    };

    const handleCertInputChange = (e) => {
        const { name, value } = e.target;
        setNewCert((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
            if (!validTypes.includes(file.type)) {
                toast.error("Please upload PDF, JPG, or PNG files only");
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error("File size should not exceed 5MB");
                return;
            }

            // Convert to base64
            const reader = new FileReader();
            reader.onloadend = () => {
                setNewCert((prev) => ({
                    ...prev,
                    file: {
                        name: file.name,
                        type: file.type,
                        data: reader.result,
                    },
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddCertification = () => {
        if (!newCert.name || !newCert.issuedBy || !newCert.year) {
            toast.error("Please fill all certification details");
            return;
        }

        const certification = {
            id: Date.now(),
            ...newCert,
        };

        setFormData((prev) => ({
            ...prev,
            certifications: [...prev.certifications, certification],
        }));

        // Reset form
        setNewCert({
            name: "",
            issuedBy: "",
            year: "",
            file: null,
        });
        setShowCertModal(false);
        toast.success("Certification added successfully!");
    };

    const handleRemoveCertification = (certId) => {
        setFormData((prev) => ({
            ...prev,
            certifications: prev.certifications.filter((cert) => cert.id !== certId),
        }));
        toast.success("Certification removed");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.experience) {
            toast.error("Please enter your years of experience");
            return;
        }

        if (formData.specializations.length === 0) {
            toast.error("Please select at least one specialization");
            return;
        }

        setLoading(true);

        try {
            const payload = {
                ...formData,
                skills: formData.skills.map(s => (s.id ? String(s.id) : String(s)))
            };

            // Call API to update professional details
            const response = await professionalAPI.updateProfessional(payload);

            if (response.data.success) {
                toast.success("Professional details updated successfully!");

                // Update form data with response
                setFormData({
                    experience: response.data.data.experience || "",
                    specializations: response.data.data.specializations || [],
                    skills: response.data.data.skills || [],
                    certifications: response.data.data.certifications || [],
                    achievements: response.data.data.achievements || "",
                });
            } else {
                toast.error(response.data.message || "Failed to update details");
            }
        } catch (error) {
            console.error("Error updating professional details:", error);
            toast.error(
                error.response?.data?.message ||
                "Failed to update details. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const getCompletionPercentage = () => {
        let completed = 0;
        const fields = [
            formData.experience,
            formData.specializations.length > 0,
            formData.certifications.length > 0,
            formData.achievements,
        ];

        fields.forEach((field) => {
            if (field) completed++;
        });

        return Math.round((completed / fields.length) * 100);
    };

    const completionPercentage = getCompletionPercentage();

    return (
        <DashboardLayout activeTab="professional">
            <div className="professional-tab">
                <div className="tab-header">
                    <div>
                        <h3>
                            <i className="bi bi-award-fill me-2"></i>
                            Professional Details
                        </h3>
                        <p className="text-muted">Showcase your expertise and qualifications</p>
                    </div>
                    <div className="completion-badge">
                        <div className="mini-progress-circle">
                            <svg width="60" height="60">
                                <circle cx="30" cy="30" r="25" fill="none" stroke="#e9ecef" strokeWidth="5" />
                                <circle
                                    cx="30"
                                    cy="30"
                                    r="25"
                                    fill="none"
                                    stroke="#cc0000"
                                    strokeWidth="5"
                                    strokeDasharray={`${(completionPercentage / 100) * 157} 157`}
                                    transform="rotate(-90 30 30)"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="mini-percentage">{completionPercentage}%</div>
                        </div>
                    </div>
                </div>

                {fetchingData ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-danger" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mt-3">Loading professional details...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="profile-form">
                        {/* Experience Section */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-briefcase-fill me-2"></i>
                                Years of Experience <span className="text-danger">*</span>
                            </h5>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label htmlFor="experience" className="form-label">
                                        Total Years in Fitness Industry
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="experience"
                                        name="experience"
                                        value={formData.experience}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 5"
                                        min="0"
                                        max="50"
                                        required
                                    />
                                    <small className="text-muted">
                                        Include all your experience as a fitness professional
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Specializations Section */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-star-fill me-2"></i>
                                Specializations <span className="text-danger">*</span>
                            </h5>
                            <p className="text-muted mb-3">
                                Select all areas where you have expertise (minimum 1 required)
                            </p>

                            <div className="specialization-grid">
                                {specializationOptions.map((spec) => (
                                    <div
                                        key={spec.id}
                                        className={`specialization-card ${formData.specializations.find((s) => s.id === spec.id) ? "selected" : ""
                                            }`}
                                        onClick={() => handleSpecializationToggle(spec)}
                                    >
                                        <div className="spec-icon">
                                            <i className={spec.icon}></i>
                                        </div>
                                        <div className="spec-name">{spec.name}</div>
                                        {formData.specializations.find((s) => s.id === spec.id) && (
                                            <div className="spec-check">
                                                <i className="bi bi-check-circle-fill"></i>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {formData.specializations.length > 0 && (
                                <div className="selected-count mt-3">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <strong>{formData.specializations.length}</strong> specialization
                                    {formData.specializations.length > 1 ? "s" : ""} selected
                                </div>
                            )}
                        </div>

                        {/* Skills Section */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-tools me-2"></i>
                                Skills
                            </h5>
                            <p className="text-muted mb-3">
                                Select your professional skills
                            </p>

                            <div className="specialization-grid">
                                {availableSkills.map((skill) => {
                                    const isSelected = formData.skills.some((s) => (s.id || s) == skill.id);
                                    return (
                                        <div
                                            key={skill.id}
                                            className={`specialization-card ${isSelected ? "selected" : ""}`}
                                            onClick={() => handleSkillToggle(skill)}
                                        >
                                            <div className="spec-name text-center w-100">{skill.name}</div>
                                            {isSelected && (
                                                <div className="spec-check">
                                                    <i className="bi bi-check-circle-fill"></i>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {availableSkills.length === 0 && (
                                <p className="text-muted fst-italic">No skills available to select.</p>
                            )}
                        </div>

                        {/* Certifications Section */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-patch-check-fill me-2"></i>
                                Certifications
                            </h5>
                            <p className="text-muted mb-3">
                                Add your fitness certifications to build credibility
                            </p>

                            <button
                                type="button"
                                className="btn btn-outline-danger mb-3"
                                onClick={() => setShowCertModal(true)}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Add Certification
                            </button>

                            {formData.certifications.length > 0 ? (
                                <div className="certifications-list">
                                    {formData.certifications.map((cert) => (
                                        <div key={cert.id} className="certification-card">
                                            <div className="cert-icon">
                                                <i className="bi bi-award-fill"></i>
                                            </div>
                                            <div className="cert-details">
                                                <h6>{cert.name}</h6>
                                                <p className="cert-issuer">
                                                    <i className="bi bi-building me-1"></i>
                                                    {cert.issuedBy}
                                                </p>
                                                <p className="cert-year">
                                                    <i className="bi bi-calendar3 me-1"></i>
                                                    {cert.year}
                                                </p>
                                                {cert.file && (
                                                    <p className="cert-file">
                                                        <i className="bi bi-file-earmark-pdf me-1"></i>
                                                        {cert.file.name}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger cert-remove"
                                                onClick={() => handleRemoveCertification(cert.id)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <i className="bi bi-award" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                                    <p className="text-muted mt-2">No certifications added yet</p>
                                </div>
                            )}
                        </div>

                        {/* Achievements Section */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-trophy-fill me-2"></i>
                                Achievements & Highlights
                            </h5>
                            <p className="text-muted mb-3">
                                Share your notable achievements, awards, or success stories
                            </p>

                            <textarea
                                className="form-control"
                                id="achievements"
                                name="achievements"
                                rows="5"
                                value={formData.achievements}
                                onChange={handleInputChange}
                                placeholder="Example:&#10;• Trained 100+ clients to achieve their fitness goals&#10;• Winner of Best Fitness Trainer Award 2023&#10;• Helped 50+ clients lose over 500kg combined&#10;• Featured in Fitness Magazine&#10;• Certified Nutrition Specialist"
                            ></textarea>
                        </div>

                        {/* Action Buttons */}
                        <div className="form-actions">
                            <button
                                type="submit"
                                className="btn btn-danger btn-lg"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle me-2"></i>
                                        Save Professional Details
                                    </>
                                )}
                            </button>
                            {completionPercentage < 75 && (
                                <p className="text-warning mt-2 mb-0">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Complete all fields for better profile visibility
                                </p>
                            )}
                        </div>
                    </form>
                )}

                {/* Certification Modal */}
                {showCertModal && (
                    <div className="modal-overlay" onClick={() => setShowCertModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h5>
                                    <i className="bi bi-award me-2"></i>
                                    Add Certification
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowCertModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="certName" className="form-label">
                                        Certification Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="certName"
                                        name="name"
                                        value={newCert.name}
                                        onChange={handleCertInputChange}
                                        placeholder="e.g., Certified Personal Trainer (CPT)"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="certIssuer" className="form-label">
                                        Issued By <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="certIssuer"
                                        name="issuedBy"
                                        value={newCert.issuedBy}
                                        onChange={handleCertInputChange}
                                        placeholder="e.g., NASM, ACE, ISSA"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="certYear" className="form-label">
                                        Year Obtained <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="certYear"
                                        name="year"
                                        value={newCert.year}
                                        onChange={handleCertInputChange}
                                        placeholder="e.g., 2020"
                                        min="1980"
                                        max={new Date().getFullYear()}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="certFile" className="form-label">
                                        Upload Certificate (Optional)
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        id="certFile"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={handleFileChange}
                                    />
                                    <small className="text-muted">
                                        Accepted formats: PDF, JPG, PNG (Max 5MB)
                                    </small>
                                    {newCert.file && (
                                        <div className="file-preview mt-2">
                                            <i className="bi bi-file-earmark-check text-success me-2"></i>
                                            {newCert.file.name}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowCertModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleAddCertification}
                                >
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add Certification
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
